/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Connection Validation & Cycle Detection
   ═══════════════════════════════════════════════════════════════ */

import type { CanonicalGraph, NodeId, PortId, PortType, RuntimeGraphIndexes } from './types';
import { getPortKey } from './indexes';

export interface ValidationResult {
  readonly valid: boolean;
  readonly reason?: string;
}

const TYPE_COMPATIBILITY_MAP: Record<PortType, readonly PortType[]> = {
  any: ['color', 'number', 'image', 'mask', 'text', 'boolean', 'any'],
  color: ['color', 'any'],
  number: ['number', 'any'],
  image: ['image', 'any'],
  mask: ['mask', 'image', 'any'], // masks can be used as grayscale single-channel images
  text: ['text', 'any'],
  boolean: ['boolean', 'number', 'any'], // booleans can be cast to 0 or 1
};

/**
 * Checks if a source output port can connect to a target input port by type.
 */
export function arePortTypesCompatible(sourceType: PortType, targetType: PortType): boolean {
  if (sourceType === 'any' || targetType === 'any') return true;
  const allowed = TYPE_COMPATIBILITY_MAP[sourceType];
  return allowed ? allowed.includes(targetType) : false;
}

/**
 * Checks if adding an edge from sourceNodeId -> targetNodeId would create a cycle.
 * Uses index-accelerated DFS.
 */
export function wouldCreateCycle(
  indexes: RuntimeGraphIndexes,
  sourceNodeId: NodeId,
  targetNodeId: NodeId
): boolean {
  // If adding edge source -> target, a cycle occurs if target can already reach source.
  if (sourceNodeId === targetNodeId) return true;

  const visited = new Set<NodeId>();
  const stack: NodeId[] = [targetNodeId];

  while (stack.length > 0) {
    const current = stack.pop()!;
    if (current === sourceNodeId) return true;

    if (!visited.has(current)) {
      visited.add(current);
      const downstream = indexes.directDownstream[current] || [];
      for (const next of downstream) {
        if (!visited.has(next)) {
          stack.push(next);
        }
      }
    }
  }

  return false;
}

/**
 * Validates whether a new connection can be formed between two ports.
 */
export function validateConnection(
  graph: CanonicalGraph,
  indexes: RuntimeGraphIndexes,
  sourceNodeId: NodeId,
  sourcePortId: PortId,
  targetNodeId: NodeId,
  targetPortId: PortId
): ValidationResult {
  // 1. Self-connection check
  if (sourceNodeId === targetNodeId) {
    return { valid: false, reason: 'Cannot connect a node to itself.' };
  }

  // 2. Node existence check
  const sourceNode = graph.nodes[sourceNodeId];
  const targetNode = graph.nodes[targetNodeId];
  if (!sourceNode) {
    return { valid: false, reason: `Source node '${sourceNodeId}' not found.` };
  }
  if (!targetNode) {
    return { valid: false, reason: `Target node '${targetNodeId}' not found.` };
  }

  // 3. Port existence and direction check
  const sourcePort = sourceNode.outputs[sourcePortId];
  const targetPort = targetNode.inputs[targetPortId];
  if (!sourcePort) {
    return { valid: false, reason: `Output port '${sourcePortId}' not found on source node.` };
  }
  if (!targetPort) {
    return { valid: false, reason: `Input port '${targetPortId}' not found on target node.` };
  }

  // 4. Type compatibility check
  if (!arePortTypesCompatible(sourcePort.type, targetPort.type)) {
    return {
      valid: false,
      reason: `Incompatible port types: '${sourcePort.type}' cannot connect to '${targetPort.type}'.`,
    };
  }

  // 5. Duplicate edge check
  const targetPortKey = getPortKey(targetNodeId, targetPortId);
  const existingConnectedEdgeIds = indexes.portConnections[targetPortKey] || [];
  for (const edgeId of existingConnectedEdgeIds) {
    const edge = graph.edges[edgeId];
    if (
      edge &&
      edge.sourceNodeId === sourceNodeId &&
      edge.sourcePortId === sourcePortId &&
      edge.targetNodeId === targetNodeId &&
      edge.targetPortId === targetPortId
    ) {
      return { valid: false, reason: 'This connection already exists.' };
    }
  }

  // 6. Fan-in check on input port (single input unless port is multi)
  if (!targetPort.multi && existingConnectedEdgeIds.length > 0) {
    return {
      valid: false,
      reason: `Port '${targetPort.name}' is already connected and does not support multi-input.`,
    };
  }

  // 7. Cycle detection check
  if (wouldCreateCycle(indexes, sourceNodeId, targetNodeId)) {
    return {
      valid: false,
      reason: 'Connection rejected: would create a circular dependency cycle.',
    };
  }

  return { valid: true };
}
